import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import { AppNoInfo } from "@/layout/AppComponents";
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import AppLink from '../../../../../../routing/helpers/AppLink';
import {
  getImpactGroupType,
  getInherentImpact,
  getProbability,
  getRiskState,
  getRiskType,
} from '../../tools/totalEnergiesRiskRiskTools';

export default function InformationTab({ elementID }) {
  const { moduleBridgeHex, page, filtersHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const riskLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID]
  );
  const information = useSelector(
    (s) => s.currentPage?.informationTab?.[elementID]
  );
  const [isLoading, setIsLoading] = useState(true);

  // > LOAD INFORMATION TAB
  const loadInformation = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_totalEnergiesRisk_risk_information_tab"),
          { moduleBridgeHex, riskID: elementID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "informationTab",
                {
                  [elementID]: data,
                },
                true
              )
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID]
  );

  useEffect(() => {
    if (isLoading) loadInformation();
  }, [isLoading]);
  // < LOAD INFORMATION TAB

  const getRelevantRisk = useCallback(
    () =>
      information?.relevantRisk === null
        ? '--'
        : information?.relevantRisk === 1
        ? 'Sí'
        : 'No',
    [information]
  );

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <div className="container-fluid">
      {userBridge?.permissions?.level === 0 && (
        <div className="row">
          <div className="col-lg-12">
            <AppLink
              path="user_module_totalEnergiesRisk_risk_edit"
              params={{
                moduleBridgeHex,
                riskHex: riskLevel1?.hex,
                page,
                filtersHex,
              }}
              className="btn btn-xs btn-success float-right"
            >
              Editar
            </AppLink>
          </div>
        </div>
      )}
      <div className="row mb-4">
        <div className="col-lg-12 mb-2">
          <Title>Descripción</Title>
          {riskLevel1?.description}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Causas</Title>
          {riskLevel1?.causes}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Consecuencias</Title>
          {riskLevel1?.consequences}
        </div>
      </div>
      <div className="row">
        <div className="col-lg-6 mb-2">
          <Title>Proceso</Title>
          {information?.process}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Subroceso</Title>
          {information?.subProcess}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Tipo de riesgo</Title>
          {getRiskType(information?.riskType)}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Estado de riesgo</Title>
          {getRiskState(riskLevel1?.state)}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Tipo de impacto</Title>
          {information?.impactGroupTypes ? (
            <ul className="mb-0">
              {information?.impactGroupTypes?.map((igt) => (
                <li>{getImpactGroupType(igt)}</li>
              ))}
            </ul>
          ) : (
            '-'
          )}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Probabilidad</Title>
          {getProbability(riskLevel1?.probability)}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Impacto inherente</Title>
          {getInherentImpact(riskLevel1?.inherentImpact)}
        </div>
        <div className="col-lg-6 mb-2">
          <Title>Riesgo Relevante</Title>
          {getRelevantRisk()}
        </div>
      </div>
    </div>
  );
}

const Title = styled.h3`
  font-size: 13px;
  font-weight: bold;
  margin: 0;
`;
